import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as DockIcon } from 'assets/icons/dockPanel.svg';
import { clsx } from 'clsx';
import IconButton from 'components/IconButton/IconButton';

import { useDockRightSlot } from './context/DockRightSlotContext';
import useDraggablePanelGesture, {
  PANEL_HEIGHT, PANEL_WIDTH, PANEL_X, PANEL_Y,
} from './hooks/useDraggablePanelGesture';
import { MIN_PANEL_SIZE } from './utils/panelBounds';

import type { CSSProperties } from 'react';
import type IDraggablePanel from './interfaces/IDraggablePanel';

import styles from './scss/DraggablePanel.module.scss';

const DEFAULT_POSITION = { x: 88, y: 88 };

/** Width the panel takes while attached, read by the stylesheet's `.attached` rule. */
const PANEL_DOCKED_WIDTH = '--panel-docked-width';

/**
 * Fixed-position panel rendered in a portal, normally on `document.body`.
 *
 * The parent owns `position`, `size`, `maxSize` and `minSize`, which reach the stylesheet as custom
 * properties rather than as `top` and `left` of their own. A drag writes those properties on the
 * element directly, so moving the panel restyles one box and re-renders nothing.
 *
 * `minSize` takes the place of the default smallest usable size rather than adding to it, and the
 * stylesheet holds no floor of its own, so the same numbers bound the CSS box and the resize.
 *
 * Passing `onMove` makes the header drag the panel, and passing `onResize` puts a grip in its
 * bottom right corner; leave them out and the panel is a plain fixed box. `isVisible` only hides
 * the panel: it stays mounted and keeps the state of its children.
 *
 * `isAttached` retargets the same portal from `document.body` to the nearest `DockRightSlot`,
 * trading the float for a plain flex box in the free space; the slot's own layout gives it its
 * height and, by taking `dockedWidth`, shrinks the main content to match. Retargeting a portal
 * moves its content rather than remounting it, so neither this component nor its children reset.
 */
function DraggablePanel({
  isVisible,
  position = DEFAULT_POSITION,
  size,
  maxSize,
  minSize = MIN_PANEL_SIZE,
  title,
  children,
  className,
  contentClassName,
  onMove,
  onResize,
  headerActions,
  onClose,
  closeLabel,
  isAttached,
  dockedWidth,
  onToggleAttach,
  toggleAttachLabel,
}: IDraggablePanel) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const dockRightSlot = useDockRightSlot();
  const {
    startMove, startResize, updateGesture, endGesture,
  } = useDraggablePanelGesture({
    panelRef, maxSize, minSize, onMove, onResize,
  });

  const panelStyle = {
    [PANEL_X]: `${position.x}px`,
    [PANEL_Y]: `${position.y}px`,
    [PANEL_WIDTH]: size ? `${size.width}px` : undefined,
    [PANEL_HEIGHT]: size ? `${size.height}px` : undefined,
    [PANEL_DOCKED_WIDTH]: dockedWidth ? `${dockedWidth}px` : undefined,
    // The bounds below fence in a float; the `.attached` stylesheet rule sets its own instead.
    maxWidth: isAttached ? undefined : maxSize?.width,
    maxHeight: isAttached ? undefined : maxSize?.height,
    minWidth: isAttached ? undefined : minSize.width,
    minHeight: isAttached ? undefined : minSize.height,
  } as CSSProperties;

  const canMove = !!onMove && !isAttached;
  const canResize = !!onResize && !isAttached;
  // Hidden, `visibility: hidden` keeps the box's dimensions, so a hidden-but-attached panel skips
  // the slot rather than reserving space nothing is shown in, and floats invisibly like before.
  const isDocked = !!isAttached && isVisible && !!dockRightSlot;

  return createPortal(
    <div
      ref={panelRef}
      className={clsx(styles.root, className, {
        [styles.hidden]: !isVisible,
        [styles.attached]: isDocked,
      })}
      style={panelStyle}
      aria-hidden={!isVisible}
    >
      {title || headerActions || onToggleAttach || onClose ? (
        <div
          className={clsx(styles.header, { [styles.draggable]: canMove })}
          data-drag-handle
          onPointerDown={canMove ? startMove : undefined}
          onPointerMove={canMove ? updateGesture : undefined}
          onPointerUp={canMove ? endGesture : undefined}
          onPointerCancel={canMove ? endGesture : undefined}
        >
          <span className={styles.headerTitle}>{title}</span>

          {headerActions}

          {onToggleAttach ? (
            <IconButton
              className={clsx(styles.toggleAttach, styles.pushRight)}
              icon={<DockIcon />}
              onClick={onToggleAttach}
              ariaLabel={toggleAttachLabel}
            />
          ) : null}

          {onClose ? (
            <IconButton
              className={clsx(styles.close, { [styles.pushRight]: !onToggleAttach })}
              icon={<CloseIcon />}
              onClick={onClose}
              ariaLabel={closeLabel || t('common.close')}
            />
          ) : null}
        </div>
      ) : null}
      <div className={clsx(styles.content, contentClassName)}>
        {children}
      </div>
      {canResize ? (
        <div
          className={styles.grip}
          data-resize-handle
          aria-hidden
          onPointerDown={startResize}
          onPointerMove={updateGesture}
          onPointerUp={endGesture}
          onPointerCancel={endGesture}
        />
      ) : null}
    </div>,
    // Hidden, `visibility: hidden` keeps the box's dimensions, so a hidden-but-attached panel
    // stays out of the slot instead of reserving space nothing is shown in. Also falls back to
    // document.body if attached before the slot has mounted; that gap closes on the next commit.
    (isAttached && isVisible && dockRightSlot) || document.body,
  );
}

export default DraggablePanel;
